import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useDesign } from '../contexts/DesignContext';

interface PieChartItem {
  label: string;
  percentage: number;
}

interface PieResultDesignedProps {
  title: string;
  types: PieChartItem[];
}

const PieResultDesigned: React.FC<PieResultDesignedProps> = ({
  title,
  types
}) => {
  const { theme } = useTheme();
  const { design } = useDesign();
  const isDark = theme === 'dark';
  // SVG properties
  const size = 160;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  
  // Use the chart colors in the exact order defined in designConfig
  const chartColors = design.colors.chartColors;
  
  // Find the item with the largest percentage
  const largestItem = [...types].sort((a, b) => b.percentage - a.percentage)[0];
  
  // Create arcs with gaps between them
  const createArcPaths = () => {
    const arcs: { path: string; color: string; label: string; }[] = [];
    let startAngle = -90; // Start from the top (in degrees)
    const gapAngle = 4; // Gap between segments in degrees
    
    // Calculate total percentage for scaling
    const totalPercentage = types.reduce((sum, item) => sum + item.percentage, 0);
    
    // Use the order provided in the types array
    types.forEach((item, index) => {
      // Calculate the angle this segment should take
      const sweepAngle = (item.percentage / totalPercentage) * 360 - gapAngle;
      
      // Convert angles to radians for calculations
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = ((startAngle + sweepAngle) * Math.PI) / 180;
      
      // Calculate the points
      const startX = centerX + radius * Math.cos(startRad);
      const startY = centerY + radius * Math.sin(startRad);
      const endX = centerX + radius * Math.cos(endRad);
      const endY = centerY + radius * Math.sin(endRad);
      
      // Determine if the arc should be drawn as "large-arc"
      const largeArcFlag = sweepAngle > 180 ? 1 : 0;
      
      // Create SVG arc path
      const path = `
        M ${centerX} ${centerY}
        L ${startX} ${startY}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
        Z
      `;
      
      // Assign color from chartColors in order, cycling if there are more segments than colors
      const colorIndex = index % chartColors.length;
      
      arcs.push({
        path,
        color: chartColors[colorIndex] || '',
        label: item.label
      });
      
      // Update the start angle for the next segment
      startAngle += sweepAngle + gapAngle;
    });
    
    return arcs;
  };
  
  const arcs = createArcPaths();
  
  return (
    <div 
      className={`rounded-2xl shadow p-${design.layout.padding.card} w-full h-full`}
      style={{ backgroundColor: isDark ? design.colors.offBlack : design.colors.lightBackground }}
    >
      <div className="grid grid-cols-2 gap-4 h-full">
        {/* Left column - title and legend */}
        <div className="flex flex-col">
          <div 
            className={`text-2xl mb-${design.layout.margins.title}`}
            style={{ 
              color: isDark ? design.colors.snowWhite : design.colors.textLight.primary,
              fontFamily: design.typography.fontFamily,
              fontSize: `${design.typography.fontSizes['2xl'] * design.fontSizeMultiplier}px`,
            }}
          >
            {title}
          </div>
          
          {/* Type list */}
          <div className="flex flex-col space-y-2">
            {types.map((item, index) => {
              const colorIndex = index % chartColors.length;
              return (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-4 h-2 rounded-full"
                    style={{ backgroundColor: chartColors[colorIndex] }}
                  />
                  <span 
                    style={{ 
                      color: isDark ? design.colors.textDark.secondary : design.colors.textLight.secondary,
                      fontSize: `${design.typography.fontSizes.sm * design.fontSizeMultiplier}px`
                    }}
                  >
                    {item.label}: {item.percentage.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Right column - pie chart */}
        <div className="flex items-center justify-end">
          <div className="relative">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {/* Draw the arc segments with gaps between them */}
              {arcs.map((arc, index) => (
                <path
                  key={index}
                  d={arc.path}
                  fill={arc.color}
                  stroke="none"
                />
              ))}
              
              {/* Inner circle to create the donut effect */}
              <circle
                cx={centerX}
                cy={centerY}
                r={radius - strokeWidth}
                fill={isDark ? design.colors.darkBackgroundSecondary : design.colors.lightBackground}
              />
            </svg>
            
            {/* Center text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span 
                style={{ 
                  color: isDark ? design.colors.snowWhite : design.colors.textLight.primary,
                  fontSize: `${design.typography.fontSizes['4xl'] * design.fontSizeMultiplier}px`,
                  fontWeight: design.typography.fontWeights.bold,
                  fontFamily: design.typography.fontFamily
                }}
              >
                {Math.round(largestItem?.percentage || 0)}%
              </span>
              <span 
                style={{ 
                  color: isDark ? design.colors.textDark.tertiary : design.colors.textLight.tertiary,
                  fontSize: `${design.typography.fontSizes.sm * design.fontSizeMultiplier}px`,
                  fontFamily: design.typography.fontFamily
                }}
              >
                {largestItem?.label || ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example usage:
// const pieData = [
//   { label: 'Completed', percentage: 67.7, color: '#F59E0B' },
//   { label: 'Running', percentage: 3.4, color: '#E5E7EB' },
//   { label: 'Failed', percentage: 5.4, color: '#FEF3C7' },
//   { label: 'Pending', percentage: 9.9, color: '#92400E' },
//   { label: 'Cancelled', percentage: 13.5, color: '#D1D5DB' }
// ];
//
// <PieResultDesigned 
//   title="Job Status Distribution"
//   types={pieData}
// />

export default PieResultDesigned;